import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle, ClipboardCheck, Eye, FileText, Hourglass, Loader2, ShieldCheck,
  Sparkles, XCircle,
} from 'lucide-react';
import { agenticAPI } from '../../../../services/api';
import Forecast, { blockedBy, isFinished } from './Forecast';

/**
 * The engagement report, read from the ledger.
 *
 * Every number here is a row rather than a recollection, which is the whole
 * point of the ledger. The executive paragraph is the one part a model writes,
 * and it is generated on demand rather than automatically so a reader always
 * knows whether they are looking at prose or at facts.
 *
 * Ruled-out hypotheses get a section of their own rather than being dropped. An
 * engagement that eliminated six hypotheses did that work, and omitting it
 * invites the reader to assume those things were never examined.
 */

/**
 * Why this was ever raised.
 *
 * The evidence pairs below a finding prove the flaw and say nothing about what
 * made anyone look. On an engagement whose input was recon rather than a
 * scanner report there is no other provenance at all: a hypothesis rests on
 * "port 8080 answered", and without this line the report cannot answer the
 * first question a reader asks about it.
 *
 * Renders nothing when there is nothing to show, rather than an empty heading.
 * Older hypotheses predate the column and genuinely have no recorded
 * provenance, which is different from having none worth showing.
 */
function RaisedFrom({ lines }) {
  if (!lines?.length) return null;
  return (
    <div className="mt-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
        Raised from
      </div>
      <ul className="mt-0.5 space-y-0.5">
        {lines.map((line, i) => (
          <li key={i} className="text-xs leading-snug text-slate-600">
            {line}
          </li>
        ))}
      </ul>
    </div>
  );
}

const SEVERITY_TONE = {
  critical: 'bg-red-100 text-red-900 border-red-300',
  high: 'bg-red-50 text-red-800 border-red-200',
  medium: 'bg-amber-50 text-amber-800 border-amber-200',
  low: 'bg-sky-50 text-sky-800 border-sky-200',
  info: 'bg-slate-50 text-slate-700 border-slate-200',
};

function Finding({ f }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-slate-900">{f.title}</h4>
          <p className="mt-1 text-xs leading-snug text-slate-600">{f.description}</p>
        </div>
        <span
          className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase
            ${SEVERITY_TONE[f.severity] || SEVERITY_TONE.info}`}
        >
          {f.severity}
        </span>
      </div>

      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        <div className="rounded-md border border-slate-200 bg-slate-50 p-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
            Separation
          </div>
          <div className="font-mono text-sm font-semibold text-slate-900">
            {f.strongest_separation === null || f.strongest_separation === undefined
              ? 'n/a'
              : Number(f.strongest_separation).toFixed(2)}
          </div>
        </div>
        <div className="rounded-md border border-slate-200 bg-slate-50 p-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
            Negative control
          </div>
          <div className="text-sm font-semibold">
            {f.negative_control_passed === true ? (
              <span className="text-wasabi-700">passed</span>
            ) : f.negative_control_passed === false ? (
              <span className="text-red-700">failed</span>
            ) : (
              <span className="text-slate-500">not established</span>
            )}
          </div>
        </div>
        <div className="rounded-md border border-slate-200 bg-slate-50 p-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
            Turns spent
          </div>
          <div className="font-mono text-sm font-semibold text-slate-900">{f.turns}</div>
        </div>
      </div>

      <div className="mt-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
          Demonstrated impact
        </div>
        <p className="mt-0.5 text-xs text-slate-700">
          {f.impact_ceiling || (
            <span className="text-slate-500">
              Not established. Impact for this finding is reasoned, not demonstrated.
            </span>
          )}
        </p>
      </div>

      <RaisedFrom lines={f.raised_from} />

      {f.evidence?.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs font-medium text-slate-500 hover:text-slate-800">
            {f.evidence.length} evidence pair{f.evidence.length === 1 ? '' : 's'}
          </summary>
          <div className="mt-2 space-y-1.5">
            {f.evidence.map((e) => (
              <div key={e.id} className="rounded border border-slate-200 bg-slate-50 p-2 font-mono text-[11px]">
                {e.baseline && (
                  <div className="text-slate-500">
                    baseline {String(e.baseline.payload)} &rarr; {e.baseline.status} ({e.baseline.length}b)
                  </div>
                )}
                <div className="text-slate-900">
                  {e.group} {String(e.payload)} &rarr; {e.status} ({e.length}b, {e.elapsed_ms}ms)
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

/**
 * What the screen shows instead of a report, while there is no report to show.
 *
 * THE DEFECT THIS REPLACES. The operator opened Report on an engagement whose
 * plan v3 was sitting at the approval gate and was handed a document headed
 * "0 confirmed, 1 ruled out". Every number in it was true. The document was
 * still wrong, because a report is read as a verdict on an engagement and this
 * was a snapshot of the middle of one, and nothing on the screen said which it
 * was. He reported it twice, once as "the report renders mid-engagement" and
 * once as "the report was thin" - the same defect from both ends.
 *
 * REFUSING WOULD BE WORSE THAN LYING. An operator mid-demo may legitimately
 * want to see what the ledger holds right now. So the report is not hidden, it
 * is LABELLED: this panel first, the document behind an explicit press, and a
 * warning stapled to the top of it if they go there.
 *
 * AND IT SAYS WHAT IT IS WAITING FOR. "Still running" is the one thing the
 * operator already knew. What he could not get from this screen was which of
 * the four possible things was outstanding, and how much further it had to go.
 */
function NotReady({ overview, hypotheses, plans, onOpenPlan, onShowAnyway }) {
  const reason = blockedBy({ overview, hypotheses });
  const planAtGate = overview?.plan?.status === 'awaiting_approval';

  return (
    <div
      data-testid="report-not-ready"
      className="rounded-lg border border-slate-300 bg-slate-50 p-5"
    >
      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
        <Hourglass className="h-4 w-4 text-slate-400" />
        Not finished yet, so there is no report
      </h3>
      <p className="mt-1 max-w-2xl text-xs leading-relaxed text-slate-600">
        {reason} A report is the artifact that leaves the building, so it is
        assembled once the Lead has closed the engagement and every angle has a
        verdict. Until then the numbers below it would change under you.
      </p>

      <div className="mt-3 rounded-md border border-slate-200 bg-white p-3">
        <Forecast overview={overview} hypotheses={hypotheses} plans={plans} />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {planAtGate && (
          <button
            type="button"
            onClick={onOpenPlan}
            className="inline-flex items-center gap-1.5 rounded-lg bg-wasabi-500 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-wasabi-500/25 transition hover:bg-wasabi-600"
          >
            <ClipboardCheck className="h-4 w-4" />
            Review plan v{overview.plan.version}
          </button>
        )}
        <button
          type="button"
          onClick={onShowAnyway}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-white"
        >
          <Eye className="h-3.5 w-3.5" />
          Show the interim read anyway
        </button>
      </div>
    </div>
  );
}

export default function ReportView({
  pentestId, overview, hypotheses, plans, onOpenPlan,
}) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summarising, setSummarising] = useState(false);
  const [error, setError] = useState(null);
  //: Set by the operator pressing through the gate. Deliberately NOT sticky
  //: across a tab change: the warning is the point, and a flag that survives
  //: means the second visit silently hands over the unlabelled document.
  const [showAnyway, setShowAnyway] = useState(false);

  const finished = isFinished({ overview, hypotheses });

  const load = useCallback(async (withSummary = false) => {
    try {
      setError(null);
      setReport(await agenticAPI.report(pentestId, withSummary));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not build the report.');
    } finally {
      setLoading(false);
      setSummarising(false);
    }
  }, [pentestId]);

  //: THE FETCH IS GATED TOO, not just the render. Assembling a report is real
  //: work on the backend and the endpoint 404s on an engagement with nothing in
  //: the ledger, which used to paint "Could not build the report" over a
  //: perfectly healthy engagement that simply had not started.
  const wanted = finished || showAnyway;
  useEffect(() => { if (wanted) load(false); }, [load, wanted]);

  // THE GATE, CHECKED BEFORE ANYTHING IS FETCHED OR RENDERED. See the note on
  // `NotReady` above: a report assembled from an engagement that has not
  // finished is a true document that reads as a false one, and it is the
  // artifact that leaves the building.
  if (!finished && !showAnyway) {
    return (
      <NotReady
        overview={overview}
        hypotheses={hypotheses}
        plans={plans}
        onOpenPlan={onOpenPlan}
        onShowAnyway={() => setShowAnyway(true)}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Assembling the report...
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{error}</span>
      </div>
    );
  }
  if (!report) return null;

  const t = report.totals || {};

  return (
    <div className="space-y-4" data-testid="report-body">
      {!finished && (
        <div
          data-testid="report-interim-warning"
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <span className="font-semibold">This is an interim read, not the report.</span>{' '}
            {blockedBy({ overview, hypotheses })} Numbers below will change. Do not
            send this to anyone.
          </span>
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="report-header">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
              <FileText className="h-4 w-4 text-slate-400" />
              {report.pentest_name}
            </h3>
            <p className="mt-0.5 text-xs text-slate-500">
              floor {report.exploit_level_floor} &middot; {t.hypotheses} angle(s) &middot;{' '}
              {t.turns} turns &middot; {t.tokens?.toLocaleString()} tokens
            </p>
            {/* HOW MANY TIMES ROUND THE LOOP. The operator asked for the plan
                and iteration count on the report itself: an engagement that
                took three plans to reach two findings is a different story
                from one that took one, and neither the report nor the screen
                said which had happened. */}
            <Forecast
              overview={overview}
              hypotheses={hypotheses}
              plans={plans}
              className="mt-1.5"
            />
          </div>
          <button
            type="button"
            disabled={summarising}
            onClick={() => { setSummarising(true); load(true); }}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {summarising ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            Write executive summary
          </button>
        </div>

        {report.executive_summary && (
          <p className="mt-3 border-l-2 border-wasabi-400 pl-3 text-sm leading-relaxed text-slate-800">
            {report.executive_summary}
          </p>
        )}

        <div className="mt-3 flex flex-wrap gap-4 border-t border-slate-100 pt-3 text-xs">
          <span className="text-red-700">{t.confirmed} confirmed</span>
          <span className="text-slate-600">{t.ruled_out} ruled out</span>
          <span className="text-amber-700">{t.unfinished} unfinished</span>
        </div>
      </div>

      <section>
        <h4 className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-500">
          <ShieldCheck className="h-3.5 w-3.5" /> Confirmed, having survived review
        </h4>
        {report.confirmed.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-300 p-4 text-sm text-slate-500">
            Nothing was confirmed. That is a result, not a gap: each angle below
            was investigated and did not hold up.
          </p>
        ) : (
          <div className="space-y-2">
            {report.confirmed.map((f) => <Finding key={f.hypothesis_id} f={f} />)}
          </div>
        )}
      </section>

      {report.ruled_out.length > 0 && (
        <section>
          <h4 className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-500">
            <XCircle className="h-3.5 w-3.5" /> Ruled out
          </h4>
          <div className="space-y-1.5">
            {report.ruled_out.map((r) => (
              <div key={r.hypothesis_id} className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="text-sm font-medium text-slate-900">{r.title}</div>
                <p className="mt-0.5 text-xs leading-snug text-slate-600">{r.reason}</p>
                <RaisedFrom lines={r.raised_from} />
                <div className="mt-1 text-[11px] text-slate-400">{r.turns} turns</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {report.unfinished.length > 0 && (
        <section>
          <h4 className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">
            Not concluded
          </h4>
          <div className="space-y-1.5">
            {report.unfinished.map((u) => (
              <div key={u.hypothesis_id} className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 text-xs">
                <span className="font-medium text-slate-900">{u.title}</span>
                <span className="ml-2 text-amber-800">{u.status}</span>
                {u.blocked_reason && (
                  <p className="mt-0.5 text-slate-600">{u.blocked_reason}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="rounded-lg border border-slate-300 bg-slate-50 p-3">
        <h4 className="mb-1.5 text-xs font-bold uppercase tracking-wider text-slate-600">
          Limits of this engagement
        </h4>
        <ul className="space-y-1 text-xs leading-snug text-slate-700">
          {report.coverage_caveats.map((c, i) => (
            <li key={i} className="flex gap-1.5">
              <span className="text-slate-400">&middot;</span>
              <span>{c}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
